import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { useFinanceBook } from './useFinanceBook.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  BalanceSheetResponse,
  CashFlowStatementResponse,
  FiscalYearListResponse,
  IncomeStatementResponse,
} from '../../lib/types/finance.js';

/** Signed Rial digits; negatives shown in parentheses, the accounting convention. */
export function signedRial(digits: string, unit: AmountDisplayUnit): string {
  const value = BigInt(digits);
  return value < 0n ? `(${formatRialFa((-value).toString(), unit)})` : formatRialFa(digits, unit);
}

function errorState(error: { status: number }) {
  return (
    <StatePanel
      variant={error.status === 403 ? 'permission_denied' : 'network_error'}
      title={
        error.status === 403
          ? 'دسترسی ندارید'
          : error.status === 422
            ? 'تاریخ‌ها نامعتبر است'
            : 'گزارش قابل نمایش نیست'
      }
    />
  );
}

function Section(props: {
  readonly title: string;
  readonly rows: readonly { accountRef: string; accountName: string; amount: string }[];
  readonly total: string;
  readonly totalLabel: string;
  readonly unit: AmountDisplayUnit;
  readonly extra?: ReactNode;
}) {
  return (
    <>
      <tr>
        <td colSpan={2} style={{ fontWeight: 700, paddingTop: 14 }}>
          {props.title}
        </td>
      </tr>
      {props.rows.map((r) => (
        <tr key={r.accountRef}>
          <td style={{ paddingInlineStart: 24 }}>
            <span className="ltr sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
              {r.accountRef}
            </span>{' '}
            {r.accountName}
          </td>
          <td className="sk-num">{signedRial(r.amount, props.unit)}</td>
        </tr>
      ))}
      {props.extra}
      <tr style={{ borderTop: '1px solid var(--sk-divider)' }}>
        <td style={{ fontWeight: 600 }}>{props.totalLabel}</td>
        <td className="sk-num" style={{ fontWeight: 600 }}>
          {signedRial(props.total, props.unit)}
        </td>
      </tr>
    </>
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 REPORTS — صورت سود و زیان (SOT §12.11)
 * archetype: filtered statement
 * status: PARITY — `GET …/finance/reports/income-statement`, posted only;
 *   categories come from the server's group-level code convention
 *   (4 = revenue, 5 = expense). The year's figures are shown before their
 *   year-end closing to retained earnings.
 * state reference: ready (no dates) · loading · empty · permission_denied · network_error
 */
export function IncomeStatementPage() {
  const book = useFinanceBook();
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );
  const query = `fromDate=${fromDate}&toDate=${toDate}${book.bookId ? `&bookId=${book.bookId}` : ''}`;
  const report = useApiGet<IncomeStatementResponse>(
    slug && fromDate && toDate && book.ready
      ? `${base}/api/v1/finance/reports/income-statement?${query}`
      : null,
    [slug, query],
  );
  if (!slug) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="صورت سود و زیان"
          subtitle="درآمد و هزینهٔ دوره — فقط اسناد قطعی"
          actions={
            report.status === 'ready' ? (
              <Link
                className="sk-btn sk-btn--secondary sk-btn--sm"
                to={`${base}/finance/reports/income-statement/print?${query}`}
              >
                چاپ
              </Link>
            ) : null
          }
        />
        <div style={{ padding: '0 20px 18px' }}>
          <V2FormGrid>
            <V2FormField label="سال مالی" width="medium" hint="بازهٔ تاریخ را پر می‌کند">
              <select
                className="sk-select"
                defaultValue=""
                onChange={(e) => {
                  const y =
                    years.status === 'ready'
                      ? years.data.items.find((i) => i.fiscalYearId === e.target.value)
                      : undefined;
                  if (y) {
                    setFromDate(y.startDate);
                    setToDate(y.endDate);
                  }
                }}
              >
                <option value="">انتخاب سال</option>
                {years.status === 'ready' &&
                  years.data.items.map((y) => (
                    <option key={y.fiscalYearId} value={y.fiscalYearId}>
                      {y.name}
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField label="از تاریخ" width="medium">
              <JalaliDateInput value={fromDate} onChange={setFromDate} />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={toDate} onChange={setToDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {!fromDate || !toDate ? (
        <V2EmptyState icon="finance" title="بازهٔ تاریخ را انتخاب کنید" />
      ) : null}
      {fromDate && toDate && report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? errorState(report.error) : null}
      {report.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            title={`از ${displayJalaliDate(report.data.fromDate)} تا ${displayJalaliDate(report.data.toDate)}`}
            subtitle={`ارقام به ${label}`}
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <tbody>
                <Section
                  title="درآمدها"
                  rows={report.data.revenue.map((r) => ({ ...r, amount: r.amountRial }))}
                  total={report.data.totals.revenueRial}
                  totalLabel="جمع درآمدها"
                  unit={unit}
                />
                <Section
                  title="هزینه‌ها"
                  rows={report.data.expense.map((r) => ({ ...r, amount: r.amountRial }))}
                  total={report.data.totals.expenseRial}
                  totalLabel="جمع هزینه‌ها"
                  unit={unit}
                />
                <tr style={{ borderTop: '2px solid var(--sk-fg)' }}>
                  <td style={{ fontWeight: 700 }}>
                    {BigInt(report.data.totals.netIncomeRial) >= 0n ? 'سود خالص' : 'زیان خالص'}
                  </td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {signedRial(report.data.totals.netIncomeRial, unit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 REPORTS — ترازنامه (SOT §12.11)
 * archetype: as-of statement
 * status: PARITY — `GET …/finance/reports/balance-sheet`, posted only. The
 *   current year's not-yet-closed result is its own equity line (zero once
 *   the year-end closing is posted, when it sits in retained earnings).
 * state reference: ready (no date) · loading · permission_denied · network_error
 */
export function BalanceSheetPage() {
  const book = useFinanceBook();
  const bookParam = book.bookId ? `&bookId=${book.bookId}` : '';
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [asOfDate, setAsOfDate] = useState('');
  const report = useApiGet<BalanceSheetResponse>(
    slug && asOfDate && book.ready
      ? `${base}/api/v1/finance/reports/balance-sheet?asOfDate=${asOfDate}${bookParam}`
      : null,
    [slug, asOfDate, bookParam],
  );
  if (!slug) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="ترازنامه"
          subtitle="دارایی، بدهی و حقوق مالکانه در تاریخ معین — فقط اسناد قطعی"
          actions={
            report.status === 'ready' ? (
              <Link
                className="sk-btn sk-btn--secondary sk-btn--sm"
                to={`${base}/finance/reports/balance-sheet/print?asOfDate=${asOfDate}${bookParam}`}
              >
                چاپ
              </Link>
            ) : null
          }
        />
        <div style={{ padding: '0 20px 18px' }}>
          <V2FormGrid>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={asOfDate} onChange={setAsOfDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {!asOfDate ? <V2EmptyState icon="finance" title="تاریخ را انتخاب کنید" /> : null}
      {asOfDate && report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? errorState(report.error) : null}
      {report.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            title={`ترازنامه در ${displayJalaliDate(report.data.asOfDate)}`}
            subtitle={`ارقام به ${label}`}
            actions={
              <V2Badge tone={report.data.balanced ? 'success' : 'danger'}>
                {report.data.balanced ? 'متوازن' : 'نامتوازن'}
              </V2Badge>
            }
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <tbody>
                <Section
                  title="دارایی‌ها"
                  rows={report.data.assets.map((r) => ({ ...r, amount: r.balanceRial }))}
                  total={report.data.totals.assetsRial}
                  totalLabel="جمع دارایی‌ها"
                  unit={unit}
                />
                <Section
                  title="بدهی‌ها"
                  rows={report.data.liabilities.map((r) => ({ ...r, amount: r.balanceRial }))}
                  total={report.data.totals.liabilitiesRial}
                  totalLabel="جمع بدهی‌ها"
                  unit={unit}
                />
                <Section
                  title="حقوق صاحبان سهام"
                  rows={report.data.equity.map((r) => ({ ...r, amount: r.balanceRial }))}
                  total={report.data.totals.equityWithCurrentPeriodRial}
                  totalLabel="جمع حقوق صاحبان سهام"
                  unit={unit}
                  extra={
                    <tr>
                      <td style={{ paddingInlineStart: 24 }}>سود (زیان) سال جاری — بسته‌نشده</td>
                      <td className="sk-num">
                        {signedRial(report.data.currentPeriodNetIncomeRial, unit)}
                      </td>
                    </tr>
                  }
                />
                <tr style={{ borderTop: '2px solid var(--sk-fg)' }}>
                  <td style={{ fontWeight: 700 }}>جمع بدهی‌ها و حقوق صاحبان سهام</td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {signedRial(
                      (
                        BigInt(report.data.totals.liabilitiesRial) +
                        BigInt(report.data.totals.equityWithCurrentPeriodRial)
                      ).toString(),
                      unit,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}

/**
 * Iranian Accounting Standard No. 2: صورت جریان وجوه نقد
 * Categorized into:
 * 1. فعالیت‌های عملیاتی (Operating activities)
 * 2. فعالیت‌های سرمایه‌گذاری (Investing activities)
 * 3. فعالیت‌های تأمین مالی (Financing activities)
 * Plus: خالص جریان نقد، مانده اول دوره، مانده پایان دوره و وضعیت تطبیق
 */
export function CashFlowStatementPage() {
  const book = useFinanceBook();
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );

  const query = [
    fromDate ? `fromDate=${fromDate}` : '',
    toDate ? `toDate=${toDate}` : '',
    book.bookId ? `bookId=${book.bookId}` : '',
  ]
    .filter(Boolean)
    .join('&');

  const report = useApiGet<CashFlowStatementResponse>(
    slug && fromDate && toDate && book.ready
      ? `${base}/api/v1/finance/reports/cash-flow?${query}`
      : null,
    [slug, fromDate, toDate, book.bookId],
  );

  if (!slug) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="صورت جریان وجوه نقد (استاندارد ۲ ایران)"
          subtitle="جریان‌های نقدی ناشی از فعالیت‌های عملیاتی، سرمایه‌گذاری و تأمین مالی — فقط اسناد قطعی"
        />
        <div style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {years.status === 'ready' && years.data.items.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>انتخاب سال مالی:</span>
              {years.data.items.map((y) => (
                <button
                  key={y.fiscalYearId}
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  onClick={() => {
                    setFromDate(y.startDate.slice(0, 10));
                    setToDate(y.endDate.slice(0, 10));
                  }}
                >
                  {y.name}
                </button>
              ))}
            </div>
          ) : null}
          <V2FormGrid>
            <V2FormField label="از تاریخ" width="medium">
              <JalaliDateInput value={fromDate} onChange={setFromDate} />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={toDate} onChange={setToDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {!fromDate || !toDate ? (
        <V2EmptyState icon="finance" title="بازهٔ زمانی گزارش را انتخاب کنید" />
      ) : null}
      {fromDate && toDate && report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? errorState(report.error) : null}
      {report.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            title={`صورت جریان وجوه نقد از ${displayJalaliDate(report.data.fromDate)} تا ${displayJalaliDate(report.data.toDate)}`}
            subtitle={`ارقام به ${label}`}
            actions={
              <V2Badge tone={report.data.reconciled ? 'success' : 'warn'}>
                {report.data.reconciled ? 'منطبق با ترازنامه' : 'دارای انحراف نقدینگی'}
              </V2Badge>
            }
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <tbody>
                {/* 1. Operating */}
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700, paddingTop: 14 }}>
                    جریان‌های نقدی ناشی از فعالیت‌های عملیاتی
                  </td>
                </tr>
                {report.data.operatingActivities.map((r, i) => (
                  <tr key={i}>
                    <td style={{ paddingInlineStart: 24 }}>
                      {r.title}
                      {r.notes ? (
                        <span
                          style={{
                            fontSize: 11.5,
                            color: 'var(--sk-fg-muted)',
                            marginInlineStart: 8,
                          }}
                        >
                          ({r.notes})
                        </span>
                      ) : null}
                    </td>
                    <td className="sk-num">{signedRial(r.amountRial, unit)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid var(--sk-divider)' }}>
                  <td style={{ fontWeight: 600 }}>خالص جریان وجه نقد ناشی از فعالیت‌های عملیاتی</td>
                  <td className="sk-num" style={{ fontWeight: 600 }}>
                    {signedRial(report.data.totals.netOperatingRial, unit)}
                  </td>
                </tr>

                {/* 2. Investing */}
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700, paddingTop: 20 }}>
                    جریان‌های نقدی ناشی از فعالیت‌های سرمایه‌گذاری
                  </td>
                </tr>
                {report.data.investingActivities.map((r, i) => (
                  <tr key={i}>
                    <td style={{ paddingInlineStart: 24 }}>
                      {r.title}
                      {r.notes ? (
                        <span
                          style={{
                            fontSize: 11.5,
                            color: 'var(--sk-fg-muted)',
                            marginInlineStart: 8,
                          }}
                        >
                          ({r.notes})
                        </span>
                      ) : null}
                    </td>
                    <td className="sk-num">{signedRial(r.amountRial, unit)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid var(--sk-divider)' }}>
                  <td style={{ fontWeight: 600 }}>
                    خالص جریان وجه نقد ناشی از فعالیت‌های سرمایه‌گذاری
                  </td>
                  <td className="sk-num" style={{ fontWeight: 600 }}>
                    {signedRial(report.data.totals.netInvestingRial, unit)}
                  </td>
                </tr>

                {/* 3. Financing */}
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700, paddingTop: 20 }}>
                    جریان‌های نقدی ناشی از فعالیت‌های تأمین مالی
                  </td>
                </tr>
                {report.data.financingActivities.map((r, i) => (
                  <tr key={i}>
                    <td style={{ paddingInlineStart: 24 }}>
                      {r.title}
                      {r.notes ? (
                        <span
                          style={{
                            fontSize: 11.5,
                            color: 'var(--sk-fg-muted)',
                            marginInlineStart: 8,
                          }}
                        >
                          ({r.notes})
                        </span>
                      ) : null}
                    </td>
                    <td className="sk-num">{signedRial(r.amountRial, unit)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid var(--sk-divider)' }}>
                  <td style={{ fontWeight: 600 }}>
                    خالص جریان وجه نقد ناشی از فعالیت‌های تأمین مالی
                  </td>
                  <td className="sk-num" style={{ fontWeight: 600 }}>
                    {signedRial(report.data.totals.netFinancingRial, unit)}
                  </td>
                </tr>

                {/* Totals & Reconciled Cash Position */}
                <tr
                  style={{
                    borderTop: '2px solid var(--sk-fg)',
                    backgroundColor: 'var(--sk-bg-subtle)',
                  }}
                >
                  <td style={{ fontWeight: 700, padding: '10px 14px' }}>
                    خالص افزایش (کاهش) در موجودی نقد
                  </td>
                  <td className="sk-num" style={{ fontWeight: 700, padding: '10px 14px' }}>
                    {signedRial(report.data.totals.netCashFlowRial, unit)}
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingInlineStart: 24 }}>موجودی نقد و بانک در ابتدای دوره</td>
                  <td className="sk-num">{signedRial(report.data.totals.openingCashRial, unit)}</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--sk-divider)', fontWeight: 700 }}>
                  <td style={{ paddingInlineStart: 24 }}>موجودی نقد و بانک در پایان دوره</td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {signedRial(report.data.totals.closingCashRial, unit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FIN-ADV-04: Profitability by Dimension Page
// ---------------------------------------------------------------------------

interface ProfitabilityItem {
  readonly dimensionValueId: string;
  readonly dimensionCode: string;
  readonly dimensionName: string;
  readonly revenueRial: string;
  readonly expenseRial: string;
  readonly profitRial: string;
  readonly profitMarginBps: number | null;
}

interface ProfitabilityResponse {
  readonly authority: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly dimensionType: string;
  readonly hasSufficientData: boolean;
  readonly items: readonly ProfitabilityItem[];
  readonly totals: {
    readonly revenueRial: string;
    readonly expenseRial: string;
    readonly profitRial: string;
    readonly profitMarginBps: number | null;
  };
}

export function ProfitabilityPage() {
  const book = useFinanceBook();
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dimensionType, setDimensionType] = useState<
    'cost_center' | 'project' | 'customer' | 'product'
  >('cost_center');
  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );
  const query = `fromDate=${fromDate}&toDate=${toDate}&dimensionType=${dimensionType}${book.bookId ? `&bookId=${book.bookId}` : ''}`;
  const report = useApiGet<ProfitabilityResponse>(
    slug && fromDate && toDate && book.ready
      ? `${base}/api/v1/finance/reports/profitability?${query}`
      : null,
    [slug, query],
  );

  if (!slug) return null;

  const dimLabels: Record<string, string> = {
    cost_center: 'مراکز هزینه',
    project: 'پروژه‌ها',
    customer: 'مشتریان',
    product: 'محصولات و کالاها',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="سودآوری به تفکیک ابعاد"
          subtitle="تحلیل سودآوری و حاشیه سود عملیاتی — فقط اسناد قطعی"
        />
        <div style={{ padding: '0 20px 18px' }}>
          <V2FormGrid>
            <V2FormField label="بُعد تحلیلی" width="medium">
              <select
                className="sk-select"
                value={dimensionType}
                onChange={(e) =>
                  setDimensionType(
                    e.target.value as 'cost_center' | 'project' | 'customer' | 'product',
                  )
                }
              >
                <option value="cost_center">مراکز هزینه</option>
                <option value="project">پروژه‌ها</option>
                <option value="customer">مشتریان</option>
                <option value="product">محصولات و کالاها</option>
              </select>
            </V2FormField>
            <V2FormField label="سال مالی" width="medium" hint="بازهٔ تاریخ را پر می‌کند">
              <select
                className="sk-select"
                defaultValue=""
                onChange={(e) => {
                  const y =
                    years.status === 'ready'
                      ? years.data.items.find((i) => i.fiscalYearId === e.target.value)
                      : undefined;
                  if (y) {
                    setFromDate(y.startDate);
                    setToDate(y.endDate);
                  }
                }}
              >
                <option value="">انتخاب سال</option>
                {years.status === 'ready' &&
                  years.data.items.map((y) => (
                    <option key={y.fiscalYearId} value={y.fiscalYearId}>
                      {y.name}
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField label="از تاریخ" width="medium">
              <JalaliDateInput value={fromDate} onChange={setFromDate} />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={toDate} onChange={setToDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {!fromDate || !toDate ? (
        <V2EmptyState icon="finance" title="بازهٔ تاریخ را انتخاب کنید" />
      ) : null}
      {fromDate && toDate && report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? errorState(report.error) : null}
      {report.status === 'ready' ? (
        !report.data.hasSufficientData || report.data.items.length === 0 ? (
          <V2Card>
            <V2CardHeader
              title={dimLabels[dimensionType]}
              subtitle={`از ${displayJalaliDate(report.data.fromDate)} تا ${displayJalaliDate(report.data.toDate)}`}
            />
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--sk-fg-muted)' }}>
              <V2EmptyState
                icon="finance"
                title="دادهٔ کافی وجود ندارد"
                description={`در این بازهٔ زمانی، هیچ سند قطعی با بُعد «${dimLabels[dimensionType]}» ثبت نشده است.`}
              />
            </div>
          </V2Card>
        ) : (
          <V2Card>
            <V2CardHeader
              title={`سودآوری به تفکیک ${dimLabels[dimensionType]}`}
              subtitle={`از ${displayJalaliDate(report.data.fromDate)} تا ${displayJalaliDate(report.data.toDate)} | ارقام به ${label}`}
            />
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>کد</th>
                    <th>عنوان</th>
                    <th style={{ textAlign: 'left' }}>درآمد</th>
                    <th style={{ textAlign: 'left' }}>هزینه</th>
                    <th style={{ textAlign: 'left' }}>سود / زیان</th>
                    <th style={{ textAlign: 'left' }}>حاشیه سود</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.items.map((item) => (
                    <tr key={item.dimensionValueId}>
                      <td className="ltr sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                        {item.dimensionCode}
                      </td>
                      <td style={{ fontWeight: 600 }}>{item.dimensionName}</td>
                      <td className="sk-num" style={{ textAlign: 'left' }}>
                        {signedRial(item.revenueRial, unit)}
                      </td>
                      <td className="sk-num" style={{ textAlign: 'left' }}>
                        {signedRial(item.expenseRial, unit)}
                      </td>
                      <td
                        className="sk-num"
                        style={{
                          textAlign: 'left',
                          fontWeight: 700,
                          color:
                            BigInt(item.profitRial) >= 0n
                              ? 'var(--sk-success, #10b981)'
                              : 'var(--sk-danger, #ef4444)',
                        }}
                      >
                        {signedRial(item.profitRial, unit)}
                      </td>
                      <td className="sk-num" style={{ textAlign: 'left' }}>
                        {item.profitMarginBps !== null
                          ? `${(item.profitMarginBps / 100).toFixed(1)}٪`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr
                    style={{
                      borderTop: '2px solid var(--sk-fg)',
                      fontWeight: 700,
                      backgroundColor: 'var(--sk-bg-subtle)',
                    }}
                  >
                    <td colSpan={2}>جمع کل</td>
                    <td className="sk-num" style={{ textAlign: 'left' }}>
                      {signedRial(report.data.totals.revenueRial, unit)}
                    </td>
                    <td className="sk-num" style={{ textAlign: 'left' }}>
                      {signedRial(report.data.totals.expenseRial, unit)}
                    </td>
                    <td className="sk-num" style={{ textAlign: 'left' }}>
                      {signedRial(report.data.totals.profitRial, unit)}
                    </td>
                    <td className="sk-num" style={{ textAlign: 'left' }}>
                      {report.data.totals.profitMarginBps !== null
                        ? `${(report.data.totals.profitMarginBps / 100).toFixed(1)}٪`
                        : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </V2Card>
        )
      ) : null}
    </div>
  );
}
